import { motion, useReducedMotion } from "framer-motion";
import { fadeUp, viewport } from "@/lib/motion";
import { assets, EMPTY_FIELD } from "@/config";

/** Section wrapper — alternates near-black / charcoal, generous vertical rhythm, fade-rise on entry. */
export const Section = ({ id, tone = "noir", compact = false, className = "", children }) => (
  <section
    id={id}
    data-testid={`section-${id}`}
    className={`relative scroll-mt-20 ${tone === "charcoal" ? "bg-charcoal" : "bg-noir"} ${className}`}
  >
    <div className={`mx-auto w-full max-w-6xl px-6 sm:px-8 ${compact ? "py-16 md:py-20" : "py-24 md:py-32 lg:py-40"}`}>{children}</div>
  </section>
);

export const Reveal = ({ children, className = "", delay = 0 }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      variants={reduce ? undefined : fadeUp}
      initial={reduce ? false : "hidden"}
      whileInView="show"
      viewport={viewport}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
};

export const Headline = ({ children, className = "" }) => (
  <h2 className={`font-serif text-3xl leading-[1.1] text-cream sm:text-4xl lg:text-5xl ${className}`}>{children}</h2>
);

/** Renders config text or a calm "on request" fallback for fields not yet supplied. */
export const Field = ({ value, fallback = EMPTY_FIELD, className = "" }) =>
  value ? <span className={className}>{value}</span> : <span className={`italic text-gray2/80 ${className}`}>{fallback}</span>;

export const MicPenIcon = ({ className = "h-5 w-5" }) => (
  <img src={assets.dividerIcon} alt="" className={`${className} object-contain`} loading="lazy" />
);

/** Thin gold hairline with the mic & pen icon centred — placed between sections. */
export const Divider = () => (
  <div className="relative flex items-center justify-center bg-noir py-1" aria-hidden="true">
    <div className="hairline absolute inset-x-6 top-1/2 sm:inset-x-12" />
    <div className="relative grid h-12 w-12 place-items-center rounded-full border border-gold/40 bg-noir">
      <MicPenIcon className="h-6 w-6" />
    </div>
  </div>
);

/** Animated waveform bars */
export const Waveform = ({ bars = 24, active = true, className = "text-oxblood h-6" }) => (
  <div className={`flex items-center justify-center gap-[3px] ${className}`} aria-hidden="true">
    {Array.from({ length: bars }).map((_, i) => (
      <span
        key={i}
        className={`wave-bar h-full ${active ? "" : "still"}`}
        style={{ animationDelay: `${(i % 7) * 0.12}s`, animationDuration: `${0.9 + (i % 5) * 0.15}s` }}
      />
    ))}
  </div>
);

/** Slim always-moving waveform used as the voice divider (slow idle breathing, quicker when listening). */
export const WaveDivider = ({ bars = 72, listening = false, className = "" }) => (
  <div className={`flex h-6 items-center justify-center gap-[3px] overflow-hidden ${className}`} aria-hidden="true">
    {Array.from({ length: bars }).map((_, i) => {
      const h = 3 + Math.abs(Math.sin(i * 0.55) * Math.cos(i * 0.18)) * 15;
      return (
        <span
          key={i}
          className="wave-bar"
          style={{
            height: `${h}px`,
            background: i % 12 === 0 ? "#C9A54A" : "#6E1E28",
            opacity: listening ? 0.95 : 0.7,
            animationDuration: listening ? "0.7s" : "1.8s",
            animationDelay: `${(i % 12) * 0.1}s`,
          }}
        />
      );
    })}
  </div>
);

export const Spinner = ({ className = "h-4 w-4" }) => (
  <svg className={`spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
